import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, CheckCircle, LogOut, MapPin, Package, 
  Download, Navigation, HelpCircle,
  FileSpreadsheet, FileText, ChevronDown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const StatItem = ({ label, value, icon: Icon, colorClass, loading }) => (
  <div className="flex flex-col p-4 border-r border-gray-100 last:border-0">
    <div className="flex items-center gap-3 mb-2">
      <div className={`p-2 rounded-lg ${colorClass} bg-opacity-10`}>
        <Icon size={18} className={colorClass.replace('bg-', 'text-')} />
      </div>
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</span>
    </div>
    {loading ? (
      <div className="h-8 w-24 bg-gray-50 animate-pulse rounded"></div>
    ) : (
      <h2 className="text-2xl font-bold font-['Poppins'] text-gray-800">{value}</h2>
    )}
  </div>
);

const NavTile = ({ icon: Icon, label, iconColor, path }) => {
  const navigate = useNavigate();
  return (
    <button 
      onClick={() => navigate(path)}
      className="flex flex-col items-center justify-center p-5 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group border border-transparent hover:border-gray-100"
    >
      <div className={`mb-3 p-4 rounded-2xl transition-all duration-300 ${iconColor} bg-white shadow-sm group-hover:scale-110`}>
        <Icon size={22} />
      </div>
      <span className="text-[11px] font-bold text-gray-500 font-['Poppins'] uppercase tracking-tight group-hover:text-gray-900">{label}</span>
    </button>
  );
};

export default function CRMDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const exportToExcel = () => {
    const data = [
      { Category: 'Total Workers', Count: 1240, Status: 'Active' },
      { Category: 'Checked-In', Count: 856, Status: 'Live' },
      { Category: 'Logged-Out', Count: 384, Status: 'Offline' },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "CRM_Data.xlsx");
    setShowExport(false);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.text("CRM Dashboard - Operations Report", 14, 15);
    
    // Using autoTable directly to avoid the "is not a function" error
    autoTable(doc, {
      head: [['Metric', 'Value', 'Status']],
      body: [
        ["Total Workers", "1,240", "Active"],
        ["Checked-In", "856", "Live"],
        ["Logged-Out", "384", "Offline"],
      ],
      startY: 25,
      headStyles: { fillStyle: '#8B5CF6', textColor: [255, 255, 255] }, // Vibrant Purple Header
      alternateRowStyles: { fillColor: [249, 250, 251] },
    });

    doc.save("CRM_Report.pdf");
    setShowExport(false);
  };

  return (
    <div className="min-h-screen bg-white p-6 md:p-10 font-['Roboto']">
      
      {/* Header */}
      <header className="flex flex-col md:flex-row justify-between items-center mb-10 pb-8 border-b border-gray-100">
        <div>
          <h1 className="text-3xl font-black font-['Poppins'] text-gray-900 tracking-tight">CRM Dashboard</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Systems Operational</p>
          </div>
        </div>
        
        <div className="relative mt-6 md:mt-0" ref={exportRef}>
          <button 
            onClick={() => setShowExport(!showExport)}
            className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 text-white text-xs font-bold rounded-full hover:bg-blue-600 transition-all shadow-lg"
          >
            <Download size={14} /> Export Options <ChevronDown size={14} />
          </button>
          
          <AnimatePresence>
            {showExport && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 shadow-2xl rounded-2xl z-50 overflow-hidden"
              >
                <button onClick={exportToExcel} className="w-full flex items-center gap-3 px-4 py-4 text-xs font-bold text-emerald-600 hover:bg-emerald-50 transition-colors text-left">
                  <FileSpreadsheet size={18} /> Excel Spreadsheet
                </button>
                <button onClick={exportToPDF} className="w-full flex items-center gap-3 px-4 py-4 text-xs font-bold text-rose-600 hover:bg-rose-50 border-t border-gray-50 transition-colors text-left">
                  <FileText size={18} /> PDF Document
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Stats Cards */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-0 mb-12 bg-gray-50/50 rounded-3xl p-2 border border-gray-100">
        <StatItem label="Total Workers" value="1,240" icon={Users} colorClass="bg-blue-500" loading={loading} />
        <StatItem label="Checked-In" value="856" icon={CheckCircle} colorClass="bg-teal-500" loading={loading} />
        <StatItem label="Logged-Out" value="384" icon={LogOut} colorClass="bg-purple-500" loading={loading} />
        <StatItem label="Active Sites" value="42" icon={MapPin} colorClass="bg-orange-500" loading={loading} />
        <StatItem label="Delivered Today" value="158" icon={Package} colorClass="bg-green-500" loading={loading} />
      </section>

      {/* Navigation Grid */}
      <section>
        <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-8 font-['Poppins']">Quick Navigation</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <NavTile icon={Navigation} label="Inventory Management" iconColor="text-indigo-500" path="/admin/Service-Warranty-Config" />
          <NavTile icon={HelpCircle} label="Helpdesk" iconColor="text-purple-500" path="/admin/help-desk" />
        </div>
      </section>
    </div>
  );
}